import React, { useEffect } from 'react';
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box,
    Typography,
    Button,
    Container,
    Paper,
    Grid
} from '@material-ui/core';
import { ThumbUp, Email } from '@material-ui/icons';
import headerImage from '../../img/header_image.jpg';

import { getPersonalProfileList } from '../../actions/personalProfileActions';
import { setRecordInfo } from '../../actions/mailActions';
import { likeUserProfile, unlikeUserProfile } from '../../actions/userActions';

import history from '../../history';
import Pagination from '../common/Pagination';
import Spinner from '../common/Spinner';
import Alert from '../../utils/alert';
import PersonalProfileCard from './PersonalProfileCard';
import { getNewNotificationNum } from '../../utils/getNotificationNum';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: 'rgb(255, 251, 249)',
        minHeight: '100vh',
    },
    pageHeader: {
        height: '30rem',
        backgroundImage: `url(${headerImage})`,
        backgroundSize: 'cover',
        marginBottom: '5rem',
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            height: '20rem',
        }
    },
    pageTitle: {
        color: 'white',
        display: 'inline-block',
        margin: 'auto',
        fontSize: '4rem',
        padding: '2rem',
        backgroundColor: 'rgba(10, 10, 10, 0.8)',
        borderRadius: '10px',
        border: '1px solid black',
        [theme.breakpoints.down('sm')]: {
            fontSize: '2.6rem',
            padding: '1.5rem',
        }
    },
    container: {
        margin: '0 5rem',
        [theme.breakpoints.down('md')]: {
            margin: '0 2.5rem',
        },
        [theme.breakpoints.down('sm')]: {
            margin: '0 1rem',
        }
    },
    infoText: {
        color: theme.palette.success.main,
        fontSize: '1.4rem',
        marginBottom: theme.spacing(1),
    },
    searchResult: {
        width: '100%',
        minHeight: '50vh',
    },
    searchResultItem: {
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: '5px',
        margin: theme.spacing(1),
        transition: 'border-color 0.2s ease',
        '&.liked': {
            border: `3px solid ${theme.palette.primary.main}`,
        },
    },
    buttonsContainer: {
        padding: theme.spacing(1),
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    likeButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey[400],
        },
    },
    unlikeButton: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.warning.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey[400],
        },
    },
    emailButton: {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.success.dark,
        },
    },
    buttonIcon: {
        marginRight: theme.spacing(0.5),
    },
}));

const DEFAULT_PAGE_SIZE = 20;

const PersonalProfileList = () => {
    const classes = useStyles();
    const profileList = useSelector(state => state.personalProfile.personalProfileList);
    const itemCount = useSelector(state => state.personalProfile.itemCount);
    const loading = useSelector(state => state.common.loading);
    const currentUser = useSelector(state => state.user.currentUser);
    const notifications = useSelector(state => state.user.notifications);
    let notificationNum = getNewNotificationNum(notifications);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getPersonalProfileList(1, DEFAULT_PAGE_SIZE));
    }, [dispatch])

    const handlePageClick = selectedPage => {
        window.scrollTo(0, 200);
        dispatch(getPersonalProfileList(selectedPage, DEFAULT_PAGE_SIZE));
    }

    const onLike = (event, userId) => {
        dispatch(likeUserProfile(userId));
    }

    const onUnlike = userId => {
        Alert.confirm('良いねを取り消しますか？')
            .then((result) => {
                if (result.value) {
                    dispatch(unlikeUserProfile(userId));
                }
            })
    }

    const onEmail = user => {
        dispatch(setRecordInfo({
            id: user._id,
            type: 'user',
            title: '',
            details: user.profile.introduction,
            userName: user.name
        }))
        history.push('/mail');
    }

    const renderProfileList = () => {
        if (!profileList) return null;

        return profileList.map(user => {
            let alreadyLiked = currentUser.likedUsers && currentUser.likedUsers.includes(user._id);

            return (
                <Paper
                    key={user._id}
                    className={`${classes.searchResultItem} ${alreadyLiked ? 'liked' : ''}`}
                    elevation={1}
                >
                    <PersonalProfileCard user={user} />
                    <Box className={classes.buttonsContainer}>
                        {alreadyLiked ? (
                            <Button
                                className={classes.unlikeButton}
                                onClick={() => onUnlike(user._id)}
                                disabled={loading}
                                variant="contained"
                                startIcon={<ThumbUp className={classes.buttonIcon} />}
                            >
                                良いねを取り消す
                            </Button>
                        ) : (
                            <Button
                                className={classes.likeButton}
                                onClick={(event) => onLike(event, user._id)}
                                disabled={loading}
                                variant="contained"
                                startIcon={<ThumbUp className={classes.buttonIcon} />}
                            >
                                良いね!
                            </Button>
                        )}
                        <Button
                            className={classes.emailButton}
                            onClick={() => onEmail(user)}
                            variant="contained"
                            startIcon={<Email className={classes.buttonIcon} />}
                        >
                            投稿者にメールを送る
                        </Button>
                    </Box>
                </Paper>
            )
        })
    }

    return (
        <div className={classes.root}>
            <Helmet>
                <title>{notificationNum ? `(${notificationNum})` : ''}求職者プロフィール</title>
                <meta name="description" content="ニュージーワークスに登録されている求職者アカウントの一覧です。（プロフィールを公開しているアカウントのみ表示されます）" />
            </Helmet>

            <Box className={classes.pageHeader}>
                <Typography variant="h1" className={classes.pageTitle}>
                    求職者プロフィール
                </Typography>
            </Box>

            <Container className={classes.container}>
                <Typography className={classes.infoText}>
                    ※プロフィールを公開しているアカウントのみ表示されます
                </Typography>
                <Typography className={classes.infoText} style={{ marginBottom: '1.5rem' }}>
                    ※良いねボタンをクリックすると対象のユーザーに通知されます。プロフィールが募集中の求人とマッチしていて、応募を促す際等にご利用ください。
                </Typography>

                <Box>
                    <Box className={classes.searchResult}>
                        {loading ? <Spinner cover={true} /> : null}
                        {renderProfileList(profileList)}
                    </Box>
                    <Pagination
                        itemCount={itemCount}
                        pageSize={DEFAULT_PAGE_SIZE}
                        onPageChange={handlePageClick}
                        className='bg-secondary-light'
                    />
                </Box>
            </Container>
        </div>
    )
}

export default PersonalProfileList
